import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  Image as ImageIcon,
  Library,
  Loader2,
  Plus,
  Save,
  Search,
  Share2,
  Trash2,
  Upload,
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, formatErr, resolveMediaUrl } from "../../lib/api";
import { SOLUTION_PAGES, defaultSeo } from "../../lib/seo";

const RECOMMENDED_IMAGE = { width: 1200, height: 630 };

const PAGE_OPTIONS = [
  { pageKey: "home", label: "Home page", sector: "Digital Signage", slug: "" },
  ...SOLUTION_PAGES.map((item) => ({ pageKey: item.pageKey, label: item.sector, sector: item.sector, slug: item.slug })),
];

const emptyLocal = {
  companyName: "RP Infotech",
  phone: "9323037254",
  address: "",
  city: "Vasai",
  state: "Maharashtra",
  pincode: "",
  serviceArea: "Vasai, Virar, Mumbai, Maharashtra, India",
  businessHours: "Mon-Sat 10:00 AM - 7:00 PM",
};

const blankImageState = { status: "idle", width: 0, height: 0, error: "" };

function localToSchema(local) {
  return JSON.stringify(
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: local.companyName,
      telephone: local.phone,
      openingHours: local.businessHours,
      areaServed: String(local.serviceArea || "").split(",").map((item) => item.trim()).filter(Boolean),
      address: {
        "@type": "PostalAddress",
        streetAddress: local.address,
        addressLocality: local.city,
        addressRegion: local.state,
        postalCode: local.pincode,
        addressCountry: "IN",
      },
      makesOffer: ["Digital Signage", "CCTV Camera", "LED TV", "LED Video Wall", "Interactive Panel"],
    },
    null,
    2,
  );
}

function schemaToLocal(raw) {
  try {
    const data = JSON.parse(raw || "{}");
    const address = data.address || {};
    return {
      ...emptyLocal,
      companyName: data.name || emptyLocal.companyName,
      phone: data.telephone || emptyLocal.phone,
      address: address.streetAddress || "",
      city: address.addressLocality || emptyLocal.city,
      state: address.addressRegion || emptyLocal.state,
      pincode: address.postalCode || "",
      serviceArea: Array.isArray(data.areaServed) ? data.areaServed.join(", ") : (data.areaServed || emptyLocal.serviceArea),
      businessHours: data.openingHours || emptyLocal.businessHours,
    };
  } catch {
    return emptyLocal;
  }
}

function buildInitial(pageKey = "home") {
  const base = defaultSeo(pageKey);
  return {
    pageKey: base.pageKey,
    sector: base.sector,
    slug: base.slug,
    metaTitle: base.metaTitle,
    metaDescription: base.metaDescription,
    metaKeywords: base.metaKeywords,
    canonicalUrl: base.canonicalUrl,
    ogTitle: base.ogTitle,
    ogDescription: base.ogDescription,
    ogImage: base.ogImage,
    twitterTitle: base.twitterTitle,
    twitterDescription: base.twitterDescription,
    twitterImage: base.twitterImage,
    h1: base.h1,
    h2: base.h2,
    robots: "index, follow",
    schemaJson: base.schemaJson,
    localBusinessJson: localToSchema(emptyLocal),
    isActive: true,
  };
}

function mediaUrl(item) {
  return resolveMediaUrl(item.public_url || (item.id ? `/api/media/serve/${item.id}` : ""));
}

function countClass(count, max) {
  return count > max ? "text-red-600" : "text-[#6B7280]";
}

function inspectImage(url) {
  const resolved = resolveMediaUrl(url);
  if (!resolved) return Promise.resolve(blankImageState);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ status: "ok", width: img.naturalWidth, height: img.naturalHeight, error: "" });
    img.onerror = () => resolve({ status: "error", width: 0, height: 0, error: "Image could not be loaded" });
    img.src = resolved;
  });
}

function ImageMeta({ state }) {
  if (state.status === "idle") return null;
  if (state.status === "loading") {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs font-semibold text-[#6B7280]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking image...
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs font-semibold text-red-600">
        <AlertTriangle className="h-3.5 w-3.5" /> Image is not reachable. Check URL or choose another image.
      </div>
    );
  }
  const tooSmall = state.width < RECOMMENDED_IMAGE.width || state.height < RECOMMENDED_IMAGE.height;
  return (
    <div className={`mt-2 flex flex-wrap items-center gap-2 text-xs font-semibold ${tooSmall ? "text-amber-700" : "text-emerald-700"}`}>
      {tooSmall ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
      <span>{state.width} x {state.height}px</span>
      <span>{tooSmall ? `Recommended ${RECOMMENDED_IMAGE.width} x ${RECOMMENDED_IMAGE.height}px or larger` : "Recommended size OK"}</span>
    </div>
  );
}

function SeoImagePicker({ label, value, imageState, disabled, onChange, onGallery, onUpload }) {
  const resolved = resolveMediaUrl(value);
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Label className="text-xs uppercase text-[#6B7280]">{label}</Label>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onGallery} className="rounded-sm bg-white">
            <Library className="mr-2 h-4 w-4" /> Choose from Media Gallery
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onUpload} className="rounded-sm bg-white">
            <Upload className="mr-2 h-4 w-4" /> Upload New Image
          </Button>
        </div>
      </div>
      <Input
        value={value || ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="mt-3 rounded-sm bg-white"
        placeholder="https://example.com/seo-image.jpg"
      />
      <div className="mt-3 grid gap-3 sm:grid-cols-[132px_1fr]">
        <div className="flex aspect-[1200/630] items-center justify-center overflow-hidden rounded-sm border border-[#D1D5DB] bg-white">
          {resolved ? (
            <img src={resolved} alt={`${label} preview`} className="h-full w-full object-cover" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-xs font-semibold text-[#6B7280]">
              <ImageIcon className="h-5 w-5" /> No image
            </div>
          )}
        </div>
        <div className="text-sm text-[#475569]">
          <div className="font-semibold text-[#111827]">Recommended social image</div>
          <p className="mt-1 leading-6">Use JPG, PNG or WEBP. Best size is {RECOMMENDED_IMAGE.width} x {RECOMMENDED_IMAGE.height}px.</p>
          <ImageMeta state={imageState} />
        </div>
      </div>
    </div>
  );
}

function SocialPreviewCard({ kind, title, description, url, image, imageState }) {
  const resolved = resolveMediaUrl(image);
  const isGoogle = kind === "google";
  return (
    <div className="overflow-hidden rounded-sm border border-[#E5E7EB] bg-white shadow-sm">
      <div className="border-b border-[#E5E7EB] bg-[#F8FAFC] px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-black text-[#111827]">
          {isGoogle ? <Search className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
          {kind}
        </div>
      </div>
      <div className="p-4">
        {isGoogle ? (
          <div>
            <div className="truncate text-xs text-[#202124]">{url}</div>
            <div className="mt-1 text-lg leading-6 text-[#1A0DAB]">{title || "RP Signage"}</div>
            <p className="mt-1 text-sm leading-5 text-[#545454]">{description || "Add a meta description to preview the search result."}</p>
            {resolved ? <img src={resolved} alt="" className="mt-3 h-20 w-32 rounded-sm border object-cover" /> : null}
          </div>
        ) : (
          <div className="rounded-sm border border-[#D1D5DB] bg-[#F8FAFC]">
            <div className="flex aspect-[1200/630] items-center justify-center overflow-hidden bg-[#E5E7EB]">
              {imageState.status === "loading" ? (
                <Loader2 className="h-7 w-7 animate-spin text-[#64748B]" />
              ) : imageState.status === "error" ? (
                <div className="flex flex-col items-center gap-2 text-sm font-semibold text-red-600">
                  <AlertTriangle className="h-6 w-6" /> Broken image
                </div>
              ) : resolved ? (
                <img src={resolved} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="text-sm font-semibold text-[#64748B]">No image selected</div>
              )}
            </div>
            <div className="p-3">
              <div className="truncate text-xs uppercase text-[#64748B]">{url}</div>
              <div className="mt-1 line-clamp-2 text-sm font-black text-[#111827]">{title || "RP Signage"}</div>
              <p className="mt-1 line-clamp-2 text-xs leading-5 text-[#475569]">{description || "Add a social description to preview this card."}</p>
            </div>
          </div>
        )}
        <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
          <div className={countClass((title || "").length, kind === "twitter" ? 70 : 60)}>
            Title {(title || "").length}/{kind === "twitter" ? 70 : 60}
          </div>
          <div className={countClass((description || "").length, kind === "twitter" ? 200 : 160)}>
            Description {(description || "").length}/{kind === "twitter" ? 200 : 160}
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewTabs({ form, imageState }) {
  const url = form.canonicalUrl || `https://rpsignage.com/${form.slug ? `solutions/${form.slug}` : ""}`.replace(/\/$/, "");
  return (
    <Tabs defaultValue="google" className="rounded-sm border border-[#E5E7EB] bg-white p-4">
      <TabsList className="grid h-auto w-full grid-cols-2 rounded-sm bg-[#F1F5F9] lg:grid-cols-4">
        <TabsTrigger value="google" className="rounded-sm">Google</TabsTrigger>
        <TabsTrigger value="facebook" className="rounded-sm">Facebook</TabsTrigger>
        <TabsTrigger value="whatsapp" className="rounded-sm">WhatsApp</TabsTrigger>
        <TabsTrigger value="twitter" className="rounded-sm">Twitter</TabsTrigger>
      </TabsList>
      <TabsContent value="google" className="mt-4">
        <SocialPreviewCard kind="google" title={form.metaTitle} description={form.metaDescription} url={url} image={form.ogImage} imageState={imageState.ogImage} />
      </TabsContent>
      <TabsContent value="facebook" className="mt-4">
        <SocialPreviewCard kind="facebook" title={form.ogTitle || form.metaTitle} description={form.ogDescription || form.metaDescription} url={url} image={form.ogImage} imageState={imageState.ogImage} />
      </TabsContent>
      <TabsContent value="whatsapp" className="mt-4">
        <SocialPreviewCard kind="whatsapp" title={form.ogTitle || form.metaTitle} description={form.ogDescription || form.metaDescription} url={url} image={form.ogImage} imageState={imageState.ogImage} />
      </TabsContent>
      <TabsContent value="twitter" className="mt-4">
        <SocialPreviewCard kind="twitter" title={form.twitterTitle || form.ogTitle || form.metaTitle} description={form.twitterDescription || form.ogDescription || form.metaDescription} url={url} image={form.twitterImage || form.ogImage} imageState={imageState.twitterImage} />
      </TabsContent>
    </Tabs>
  );
}

export default function AdminSeo() {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(buildInitial("home"));
  const [local, setLocal] = useState(emptyLocal);
  const [saving, setSaving] = useState(false);
  const [sameSocialImage, setSameSocialImage] = useState(true);
  const [imageState, setImageState] = useState({ ogImage: blankImageState, twitterImage: blankImageState });
  const [mediaItems, setMediaItems] = useState([]);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaTarget, setMediaTarget] = useState("ogImage");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [mediaSearch, setMediaSearch] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/seo");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail || e));
    }
  };

  const loadMedia = async () => {
    setMediaLoading(true);
    try {
      const { data } = await api.get("/admin/seo/media");
      setMediaItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail || e));
    } finally {
      setMediaLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    let cancelled = false;
    const check = async (field, value) => {
      if (!value) {
        if (!cancelled) setImageState((current) => ({ ...current, [field]: blankImageState }));
        return;
      }
      setImageState((current) => ({ ...current, [field]: { ...blankImageState, status: "loading" } }));
      const result = await inspectImage(value);
      if (!cancelled) setImageState((current) => ({ ...current, [field]: result }));
    };
    check("ogImage", form.ogImage);
    check("twitterImage", form.twitterImage || form.ogImage);
    return () => { cancelled = true; };
  }, [form.ogImage, form.twitterImage]);

  const selectedOption = PAGE_OPTIONS.find((item) => item.pageKey === form.pageKey) || PAGE_OPTIONS[0];

  const setField = (key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "ogImage" && sameSocialImage) next.twitterImage = value;
      return next;
    });
  };

  const choosePage = (pageKey) => {
    const existing = items.find((item) => item.pageKey === pageKey);
    if (existing) {
      setEditingId(existing.id);
      setForm({ ...buildInitial(pageKey), ...existing });
      setSameSocialImage(!existing.twitterImage || existing.twitterImage === existing.ogImage);
      setLocal(schemaToLocal(existing.localBusinessJson));
      return;
    }
    setEditingId("");
    const next = buildInitial(pageKey);
    setForm(next);
    setSameSocialImage(!next.twitterImage || next.twitterImage === next.ogImage);
    setLocal(schemaToLocal(next.localBusinessJson));
  };

  const payload = useMemo(
    () => ({
      ...form,
      twitterImage: sameSocialImage ? form.ogImage : form.twitterImage,
      localBusinessJson: localToSchema(local),
    }),
    [form, local, sameSocialImage],
  );

  const openGallery = async (field) => {
    setMediaTarget(field);
    setMediaOpen(true);
    if (mediaItems.length === 0) await loadMedia();
  };

  const triggerUpload = (field) => {
    setMediaTarget(field);
    fileRef.current?.click();
  };

  const uploadSeoImage = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Please upload JPG, PNG or WEBP image only");
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", file.name);
      fd.append("folder", "seo");
      const { data } = await api.post("/admin/seo/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = mediaUrl(data);
      setField(mediaTarget, url);
      toast.success("SEO image uploaded");
      await loadMedia();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail || e));
    } finally {
      setUploadingImage(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const selectMedia = (item) => {
    const url = mediaUrl(item);
    setField(mediaTarget, url);
    setMediaOpen(false);
  };

  const save = async (e) => {
    e.preventDefault();
    const twitterStatus = sameSocialImage ? imageState.ogImage : imageState.twitterImage;
    if (form.ogImage && imageState.ogImage.status === "error") {
      toast.error("Open Graph image is not reachable. Please fix it before saving.");
      return;
    }
    if ((sameSocialImage ? form.ogImage : form.twitterImage) && twitterStatus.status === "error") {
      toast.error("Twitter image is not reachable. Please fix it before saving.");
      return;
    }
    setSaving(true);
    try {
      const { data } = editingId
        ? await api.put(`/admin/seo/${editingId}`, payload)
        : await api.post("/admin/seo", payload);
      setEditingId(data.id);
      setForm({ ...buildInitial(data.pageKey), ...data });
      setSameSocialImage(!data.twitterImage || data.twitterImage === data.ogImage);
      setLocal(schemaToLocal(data.localBusinessJson));
      toast.success("SEO settings saved");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail || e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete SEO settings for ${item.pageKey}?`)) return;
    try {
      await api.delete(`/admin/seo/${item.id}`);
      toast.success("SEO settings deleted");
      if (editingId === item.id) choosePage("home");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail || e));
    }
  };

  const filteredMedia = mediaItems.filter((item) => String(item.name || item.original_filename || "").toLowerCase().includes(mediaSearch.toLowerCase()));

  return (
    <div data-testid="admin-seo-page">
      <PageHeader
        overline="Admin / SEO"
        title="SEO settings."
        subtitle="Control crawlable metadata, social previews, schema and local SEO for public RP Signage pages."
      >
        <Button onClick={() => choosePage("home")} className="rounded-sm bg-[#111827] text-white hover:bg-[#374151]">
          <Plus className="mr-2 h-4 w-4" /> New / Reset
        </Button>
      </PageHeader>

      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadSeoImage(e.target.files?.[0])} />

      <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
        <form onSubmit={save} className="space-y-5 rounded-sm border border-[#E5E7EB] bg-white p-5">
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label className="text-xs uppercase text-[#6B7280]">Page / sector</Label>
              <select
                value={form.pageKey}
                onChange={(e) => choosePage(e.target.value)}
                className="mt-1 h-10 w-full rounded-sm border border-[#D1D5DB] bg-white px-3 text-sm"
              >
                {PAGE_OPTIONS.map((item) => <option key={item.pageKey} value={item.pageKey}>{item.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase text-[#6B7280]">Sector</Label>
              <Input value={form.sector || selectedOption.sector} onChange={(e) => setField("sector", e.target.value)} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase text-[#6B7280]">SEO slug</Label>
              <Input value={form.slug || ""} onChange={(e) => setField("slug", e.target.value)} className="mt-1 rounded-sm" placeholder="hotel-restaurant-signage" />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="flex justify-between gap-3">
                <Label className="text-xs uppercase text-[#6B7280]">Meta title</Label>
                <span className={`text-xs ${countClass(form.metaTitle.length, 60)}`}>{form.metaTitle.length}/60</span>
              </div>
              <Input value={form.metaTitle} onChange={(e) => setField("metaTitle", e.target.value)} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase text-[#6B7280]">Canonical URL</Label>
              <Input value={form.canonicalUrl} onChange={(e) => setField("canonicalUrl", e.target.value)} className="mt-1 rounded-sm" />
            </div>
          </div>

          <div>
            <div className="flex justify-between gap-3">
              <Label className="text-xs uppercase text-[#6B7280]">Meta description</Label>
              <span className={`text-xs ${countClass(form.metaDescription.length, 160)}`}>{form.metaDescription.length}/160</span>
            </div>
            <Textarea value={form.metaDescription} onChange={(e) => setField("metaDescription", e.target.value)} className="mt-1 rounded-sm" rows={3} />
          </div>

          <div>
            <Label className="text-xs uppercase text-[#6B7280]">Meta keywords</Label>
            <Textarea value={form.metaKeywords} onChange={(e) => setField("metaKeywords", e.target.value)} className="mt-1 rounded-sm" rows={2} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label className="text-xs uppercase text-[#6B7280]">H1</Label>
              <Input value={form.h1} onChange={(e) => setField("h1", e.target.value)} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase text-[#6B7280]">H2</Label>
              <Input value={form.h2} onChange={(e) => setField("h2", e.target.value)} className="mt-1 rounded-sm" />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label className="text-xs uppercase text-[#6B7280]">OG title</Label>
              <Input value={form.ogTitle} onChange={(e) => setField("ogTitle", e.target.value)} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase text-[#6B7280]">Robots</Label>
              <select value={form.robots} onChange={(e) => setField("robots", e.target.value)} className="mt-1 h-10 w-full rounded-sm border border-[#D1D5DB] bg-white px-3 text-sm">
                <option value="index, follow">index, follow</option>
                <option value="noindex, follow">noindex, follow</option>
                <option value="index, nofollow">index, nofollow</option>
                <option value="noindex, nofollow">noindex, nofollow</option>
              </select>
            </div>
            <div className="flex items-end gap-3 pb-2">
              <Switch checked={form.isActive} onCheckedChange={(checked) => setField("isActive", checked)} />
              <span className="text-sm font-semibold">Active for public pages</span>
            </div>
          </div>
          <div>
            <div className="flex justify-between gap-3">
              <Label className="text-xs uppercase text-[#6B7280]">OG description</Label>
              <span className={`text-xs ${countClass(form.ogDescription.length, 160)}`}>{form.ogDescription.length}/160</span>
            </div>
            <Textarea value={form.ogDescription} onChange={(e) => setField("ogDescription", e.target.value)} className="mt-1 rounded-sm" rows={2} />
          </div>

          <SeoImagePicker
            label="Open Graph image"
            value={form.ogImage}
            imageState={imageState.ogImage}
            onChange={(value) => setField("ogImage", value)}
            onGallery={() => openGallery("ogImage")}
            onUpload={() => triggerUpload("ogImage")}
          />

          <div className="flex items-center gap-3 rounded-sm border border-[#E5E7EB] bg-white p-4">
            <Switch
              checked={sameSocialImage}
              onCheckedChange={(checked) => {
                setSameSocialImage(checked);
                if (checked) setField("twitterImage", form.ogImage);
              }}
            />
            <div>
              <div className="text-sm font-black text-[#111827]">Use same image for Open Graph and Twitter</div>
              <p className="text-xs text-[#6B7280]">Recommended for WhatsApp, Facebook and Twitter consistency.</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="flex justify-between gap-3">
                <Label className="text-xs uppercase text-[#6B7280]">Twitter title</Label>
                <span className={`text-xs ${countClass(form.twitterTitle.length, 70)}`}>{form.twitterTitle.length}/70</span>
              </div>
              <Input value={form.twitterTitle} onChange={(e) => setField("twitterTitle", e.target.value)} className="mt-1 rounded-sm" />
            </div>
            <div>
              <div className="flex justify-between gap-3">
                <Label className="text-xs uppercase text-[#6B7280]">Twitter description</Label>
                <span className={`text-xs ${countClass(form.twitterDescription.length, 200)}`}>{form.twitterDescription.length}/200</span>
              </div>
              <Input value={form.twitterDescription} onChange={(e) => setField("twitterDescription", e.target.value)} className="mt-1 rounded-sm" />
            </div>
          </div>

          <SeoImagePicker
            label="Twitter image"
            value={sameSocialImage ? form.ogImage : form.twitterImage}
            disabled={sameSocialImage}
            imageState={sameSocialImage ? imageState.ogImage : imageState.twitterImage}
            onChange={(value) => setField("twitterImage", value)}
            onGallery={() => openGallery("twitterImage")}
            onUpload={() => triggerUpload("twitterImage")}
          />

          <div className="rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-4">
            <div className="mb-3 text-sm font-black">Local business schema</div>
            <div className="grid gap-3 md:grid-cols-2">
              {[
                ["companyName", "Company name"], ["phone", "Phone"], ["address", "Address"], ["city", "City"],
                ["state", "State"], ["pincode", "Pincode"], ["serviceArea", "Service area"], ["businessHours", "Business hours"],
              ].map(([key, label]) => (
                <div key={key}>
                  <Label className="text-xs uppercase text-[#6B7280]">{label}</Label>
                  <Input value={local[key] || ""} onChange={(e) => setLocal((current) => ({ ...current, [key]: e.target.value }))} className="mt-1 rounded-sm bg-white" />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase text-[#6B7280]">JSON-LD schema field</Label>
            <Textarea value={form.schemaJson} onChange={(e) => setField("schemaJson", e.target.value)} className="mt-1 rounded-sm font-mono text-xs" rows={7} />
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving || uploadingImage} className="rounded-sm bg-[#111827] text-white hover:bg-[#374151]">
              <Save className="mr-2 h-4 w-4" /> {saving ? "Saving..." : "Save SEO settings"}
            </Button>
          </div>
        </form>

        <aside className="space-y-5">
          <PreviewTabs form={form} imageState={imageState} />

          <div className="rounded-sm border border-[#E5E7EB] bg-white p-5">
            <div className="mb-3 text-sm font-black">Saved SEO pages</div>
            <div className="space-y-2">
              {items.length === 0 && <div className="text-sm text-[#6B7280]">No saved SEO pages yet.</div>}
              {items.map((item) => (
                <div key={item.id} className="flex items-center justify-between gap-3 rounded-sm border border-[#E5E7EB] p-3">
                  <button type="button" onClick={() => choosePage(item.pageKey)} className="min-w-0 text-left">
                    <div className="truncate text-sm font-black">{item.metaTitle || item.pageKey}</div>
                    <div className="truncate text-xs text-[#6B7280]">{item.pageKey} {item.isActive ? "" : "(inactive)"}</div>
                  </button>
                  <Button type="button" variant="ghost" size="icon" onClick={() => remove(item)} className="text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {mediaOpen ? (
        <div className="fixed inset-0 z-[80] bg-black/50 p-4 backdrop-blur-sm">
          <div className="mx-auto flex max-h-[90vh] max-w-5xl flex-col rounded-sm bg-white shadow-2xl">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E5E7EB] p-4">
              <div>
                <div className="text-lg font-black text-[#111827]">Choose SEO image</div>
                <div className="text-sm text-[#6B7280]">Images come from the existing RP Signage media library.</div>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={loadMedia} disabled={mediaLoading} className="rounded-sm">
                  {mediaLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />} Refresh
                </Button>
                <Button type="button" onClick={() => setMediaOpen(false)} className="rounded-sm bg-[#111827] text-white">Close</Button>
              </div>
            </div>
            <div className="border-b border-[#E5E7EB] p-4">
              <Input value={mediaSearch} onChange={(e) => setMediaSearch(e.target.value)} className="rounded-sm" placeholder="Search media images..." />
            </div>
            <div className="overflow-y-auto p-4">
              {mediaLoading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm font-semibold text-[#64748B]">
                  <Loader2 className="h-5 w-5 animate-spin" /> Loading media gallery...
                </div>
              ) : filteredMedia.length === 0 ? (
                <div className="rounded-sm border border-dashed border-[#CBD5E1] p-8 text-center text-sm text-[#64748B]">No images found. Use Upload New Image to add one.</div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredMedia.map((item) => (
                    <button key={item.id} type="button" onClick={() => selectMedia(item)} className="group overflow-hidden rounded-sm border border-[#E5E7EB] bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-lg">
                      <div className="aspect-[1200/630] overflow-hidden bg-[#F1F5F9]">
                        <img src={mediaUrl(item)} alt={item.name || "SEO media"} className="h-full w-full object-cover transition group-hover:scale-105" />
                      </div>
                      <div className="p-3">
                        <div className="truncate text-sm font-black text-[#111827]">{item.name || item.original_filename || item.id}</div>
                        <div className="mt-1 text-xs text-[#64748B]">{item.content_type}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
